import { PICK_FRAMES_PER_TILESET, PICK_FRAMES_PER_WORLD } from "../assets/budget.ts";
import type { Command } from "./commands.ts";
import { MAX_TILESET_INDEX } from "./packed-tile.ts";
import { framePlacementsOf, tilesetUses, type TilesetEntry, type TilesetFrames, type TilesetUse } from "./tileset-change.ts";
import { framesAmong, framesUnion, framesWithout, pickedFrames, sameFrames, tilesetEntry } from "./tileset-frames.ts";
import type { TilesetRef, WorldDocument } from "./types.ts";

/**
 * What a pull, an add, a drop and a removal each do to one tileset of a world's list (ADR-129 decisions 3.4
 * and 3.8), and the one tileset change several of them make together. The local commands, the live resolver
 * and the session's undo all read these rules, so a pick made in one tab and resolved in another leave the
 * same list. Each rule is handed the entry the list holds, looked up once through `entriesById`, and a drop
 * the placements found in one pass over the props (`framePlacementsOf`), so resolving an intent costs the
 * length of the list, the props and the tiles it names, never one times another.
 */

/**
 * A tileset list as a lookup by id, built in one pass.
 *
 * @param tilesets The list.
 * @returns The entry a tileset id names, undefined when the list lacks it.
 */
export function entriesById(tilesets: readonly TilesetRef[]): (id: string) => TilesetRef | undefined {
  const by = new Map(tilesets.map((tileset) => [tileset.id, tileset]));
  return (id) => by.get(id);
}

/**
 * What one tileset's part of a change does: nothing; nothing because something placed stops it, naming that
 * use; an entry appended to the list, whole when `frames` is absent; a listed entry's frames set in place,
 * absent meaning whole; or the entry removed.
 */
export type PickStep =
  | { readonly kind: "none" }
  | { readonly kind: "held"; readonly use: TilesetUse }
  | { readonly kind: "insert"; readonly frames?: readonly number[] }
  | { readonly kind: "frames"; readonly before?: readonly number[]; readonly after?: readonly number[] }
  | { readonly kind: "remove" };

/**
 * A step of one tileset.
 */
export type PickStepOf = readonly [id: string, step: PickStep];

/**
 * The first thing in a world that names each tileset, found once however many tilesets are asked about.
 *
 * @param world The document.
 * @returns A lookup of a tileset's first use, undefined when nothing names it.
 * @throws Error, from the lookup, when a tile cell indexes past the world's tilesets.
 */
export function firstUses(world: WorldDocument): (tileset: string) => TilesetUse | undefined {
  let uses: Map<string, TilesetUse> | null = null;
  return (tileset) => {
    if (uses === null) {
      uses = new Map();
      for (const use of tilesetUses(world)) {
        if (!uses.has(use.tileset)) {
          uses.set(use.tileset, use);
        }
      }
    }
    return uses.get(tileset);
  };
}

/**
 * A pull of frames of one tileset: an unlisted tileset gains a partial entry of them; a partial entry becomes
 * the union, ascending; a whole entry, or one that already holds them all, is left.
 *
 * @param held The entry the list holds for the tileset, undefined when it lists none.
 * @param tiles The frames, ascending and distinct.
 * @returns The step.
 */
export function pullStep(held: TilesetRef | undefined, tiles: readonly number[]): PickStep {
  if (held === undefined) {
    return { kind: "insert", frames: tiles };
  }
  if (held.frames === undefined) {
    return { kind: "none" };
  }
  const after = framesUnion(held.frames, tiles);
  return after.length === held.frames.length ? { kind: "none" } : { kind: "frames", before: held.frames, after };
}

/**
 * An add of one whole tileset: an unlisted tileset gains a whole entry; a partial entry is made whole in
 * place; a whole entry is left.
 *
 * @param held The entry the list holds for the tileset, undefined when it lists none.
 * @returns The step.
 */
export function wholeStep(held: TilesetRef | undefined): PickStep {
  if (held === undefined) {
    return { kind: "insert" };
  }
  return held.frames === undefined ? { kind: "none" } : { kind: "frames", before: held.frames };
}

/**
 * A drop of frames of one tileset, each part that can be done done: of the frames named that a partial entry
 * holds, those nothing places are taken out and the placed ones stay. When that would leave the entry no
 * frame, the entry is removed if nothing names its tileset, and otherwise nothing is taken. An unlisted or
 * whole tileset is left.
 *
 * @param held The entry the list holds for the tileset, undefined when it lists none.
 * @param tiles The frames.
 * @param placed The tileset's placed frames, each with its first placement, read only when the entry holds a
 *   frame named.
 * @param named The first use of a tileset by anything at all, as `firstUses` gives it.
 * @returns The step: `held` when a placement stopped every frame it would have taken, or the entry's removal.
 */
export function dropStep(held: TilesetRef | undefined, tiles: readonly number[], placed: () => ReadonlyMap<number, TilesetUse>, named: (tileset: string) => TilesetUse | undefined): PickStep {
  if (held?.frames === undefined) {
    return { kind: "none" };
  }
  const asked = framesAmong(held.frames, tiles);
  if (asked.length === 0) {
    return { kind: "none" };
  }
  const placements = placed();
  const stopped = asked.find((tile) => placements.has(tile));
  const free = stopped === undefined ? asked : asked.filter((tile) => !placements.has(tile));
  if (free.length === 0) {
    return { kind: "held", use: placements.get(stopped as number) as TilesetUse };
  }
  const after = framesWithout(held.frames, free);
  if (after.length > 0) {
    return { kind: "frames", before: held.frames, after };
  }
  const use = named(held.id);
  return use === undefined ? { kind: "remove" } : { kind: "held", use };
}

/**
 * A removal of one tileset's entry, whole or partial: taken out when nothing names the tileset.
 *
 * @param held The entry the list holds for the tileset, undefined when it lists none.
 * @param named The first use of a tileset by anything at all, as `firstUses` gives it.
 * @returns The step.
 */
export function removeStep(held: TilesetRef | undefined, named: (tileset: string) => TilesetUse | undefined): PickStep {
  if (held === undefined) {
    return { kind: "none" };
  }
  const use = named(held.id);
  return use === undefined ? { kind: "remove" } : { kind: "held", use };
}

/**
 * The placed frames of the tilesets a change drops from, found in one pass over the world's props the first
 * time one is asked for, however many tilesets the change names.
 *
 * @param world The document.
 * @param tilesets The tilesets the change drops from.
 * @returns A lookup of one tileset's placed frames, each with its first placement.
 */
export function placementsFor(world: WorldDocument, tilesets: Iterable<string>): (tileset: string) => ReadonlyMap<number, TilesetUse> {
  let found: Map<string, Map<number, TilesetUse>> | null = null;
  const none = new Map<number, TilesetUse>();
  return (tileset) => {
    found ??= framePlacementsOf(world, new Set(tilesets));
    return found.get(tileset) ?? none;
  };
}

/**
 * The tileset list a set of steps leaves: removals taken first, frames set in place, then the appended
 * entries at the end in the steps' order.
 *
 * @param tilesets The world's list.
 * @param steps The steps, at most one a tileset.
 * @returns The list.
 */
export function listAfter(tilesets: readonly TilesetRef[], steps: readonly PickStepOf[]): TilesetRef[] {
  const by = new Map(steps);
  const kept = tilesets.flatMap((tileset) => {
    const step = by.get(tileset.id);
    if (step?.kind === "remove") {
      return [];
    }
    return [step?.kind === "frames" ? tilesetEntry(tileset.id, step.after) : tileset];
  });
  return [...kept, ...steps.flatMap(([id, step]) => (step.kind === "insert" ? [tilesetEntry(id, step.frames)] : []))];
}

/**
 * Which cap a set of pulls would pass (ADR-129 decision 6): the caps bind where picks are made, so only a
 * step that adds picked frames is measured. A world already past a cap, as a merge's union can leave it,
 * still takes an add, a drop and a removal.
 */
export type PickOverflow = { readonly cap: "tileset"; readonly id: string; readonly frames: number } | { readonly cap: "world"; readonly frames: number } | { readonly cap: "list"; readonly entries: number };

/**
 * Measures the list a set of steps leaves against the pick caps and the packed form's count of tilesets.
 *
 * @param tilesets The world's list.
 * @param steps Every step of the change.
 * @param pulls The ids whose steps come from a pull.
 * @returns The first cap passed, or null when the change is within all of them: an entry a pull made or
 *   widened past `PICK_FRAMES_PER_TILESET`, the world's picked frames past `PICK_FRAMES_PER_WORLD` when a
 *   pull added any, or more entries than the packed tile form indexes.
 */
export function pickOverflow(tilesets: readonly TilesetRef[], steps: readonly PickStepOf[], pulls: ReadonlySet<string>): PickOverflow | null {
  const after = listAfter(tilesets, steps);
  if (after.length > MAX_TILESET_INDEX + 1) {
    return { cap: "list", entries: after.length };
  }
  let added = false;
  for (const [id, step] of steps) {
    const frames = step.kind === "insert" ? step.frames : step.kind === "frames" ? step.after : undefined;
    if (!pulls.has(id) || frames === undefined) {
      continue;
    }
    added = true;
    if (frames.length > PICK_FRAMES_PER_TILESET) {
      return { cap: "tileset", id, frames: frames.length };
    }
  }
  const total = pickedFrames(after);
  return added && total > PICK_FRAMES_PER_WORLD ? { cap: "world", frames: total } : null;
}

/**
 * The one tileset change a set of steps makes: removed entries by their index with the frames they hold,
 * frames set in place, and inserted entries at the end of what is left, in the steps' order.
 *
 * @param world The document.
 * @param steps The steps, at most one a tileset; a step of `none` or `held` adds nothing.
 * @returns The command, or null when no step changes the list. A change of removals and inserts alone carries
 *   no third list.
 */
export function stepsCommand(world: WorldDocument, steps: readonly PickStepOf[]): Command | null {
  const by = new Map(steps);
  const remove: TilesetEntry[] = world.tilesets.flatMap((tileset, index) => (by.get(tileset.id)?.kind === "remove" ? [{ index, ...tilesetEntry(tileset.id, tileset.frames) }] : []));
  const kept = world.tilesets.length - remove.length;
  const insert: TilesetEntry[] = steps.flatMap(([id, step]) => (step.kind === "insert" ? [tilesetEntry(id, step.frames)] : [])).map((entry, i) => ({ index: kept + i, ...entry }));
  const frames: TilesetFrames[] = steps.flatMap(([id, step]) => (step.kind === "frames" && !sameFrames(step.before, step.after) ? [{ id, ...(step.before === undefined ? {} : { before: step.before }), ...(step.after === undefined ? {} : { after: step.after }) }] : []));
  if (remove.length === 0 && insert.length === 0 && frames.length === 0) {
    return null;
  }
  return { kind: "changeTilesets", remove, insert, ...(frames.length === 0 ? {} : { frames }) };
}
